// services/layout.service.ts
import { Injectable, signal, computed, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common'; // ← Add this line

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

@Injectable({ providedIn: 'root' })
export class LayoutService {
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  // Configurable breakpoints
  private breakpoints = {
    mobile: 0,
    tablet: 768,
    desktop: 1024,
  };

  // Initial fallback width (server-safe)
  private width = signal<number>(this.breakpoints.mobile); // Default to mobile on server

  // Derived signals (reactive on client)
  isMobile = computed(() => this.width() < this.breakpoints.tablet);
  isTablet = computed(() => 
    this.width() >= this.breakpoints.tablet && this.width() < this.breakpoints.desktop
  );
  isDesktop = computed(() => this.width() >= this.breakpoints.desktop);

  currentBreakpoint = computed<Breakpoint>(() => {
    if (this.isMobile()) return 'mobile';
    if (this.isTablet()) return 'tablet';
    return 'desktop';
  });

  constructor() {
    // Only initialize browser code if on client side
    if (isPlatformBrowser(this.platformId)) {
      const syncViewportWidth = () => {
        const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
        this.width.set(Math.round(viewportWidth));
      };

      syncViewportWidth();

      const resizeHandler = () => syncViewportWidth();
      window.addEventListener('resize', resizeHandler);
      window.visualViewport?.addEventListener('resize', resizeHandler);

      if (typeof window.matchMedia === 'function') {
        const mobileQuery = window.matchMedia(`(max-width: ${this.breakpoints.tablet - 1}px)`);
        const onBreakpointChange = () => syncViewportWidth();
        mobileQuery.addEventListener('change', onBreakpointChange);

        this.destroyRef.onDestroy(() => {
          mobileQuery.removeEventListener('change', onBreakpointChange);
        });
      }

      // Cleanup
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('resize', resizeHandler);
        window.visualViewport?.removeEventListener('resize', resizeHandler);
      });
    }
  }
}